/**
 * Assistant - conversational product guide and plot/export builder.
 *
 * The user describes a plot in natural language; the backend (`/api/assistant`)
 * drives an LLM through a tool-use loop and returns a reply plus, when ready, a
 * validated proposal or allowlisted navigation. We show proposals as cards the
 * user can Apply or opt to plot automatically. Safe navigation is handed to
 * the application shell as an in-place section/tab change.
 */

import { groupLabel, measureLabel } from '../plot/table-config.ts';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { shipLabel } from '../../utils/ships.ts';
import { activeDataSource, allColumns, selectedShip, shipMeta } from '../../state/store.ts';
import {
  assistantRailWidth,
  assistantTechnical,
  assistantSurface,
  assistantTab,
  railMax,
  setAssistantRailWidth,
  setAssistantTab,
  RAIL_MIN,
} from '../../state/assistant-surface.ts';
import { assistantMark } from './mark.ts';
import { ApiError } from '../../api/client.ts';
import {
  runAssistantChat,
  getAssistantStatus,
  recordAssistantPlotApplyClicked,
  sendAssistantFeedback,
} from '../../api/endpoints.ts';
import type { AssistantMessage, DashboardEdit, ExportSpec, PlotSpec, PlotSpecAxisTitle } from '../../api/types.ts';
import { applyDashboardEdit, buildDashboardContext, outcomeNote } from './dashboard-assist.ts';
import { buildCatalogContext } from './catalog-assist.ts';
import { applyProposedPlot } from './apply-plot.ts';
import { applyProposedExport } from './apply-export.ts';
import {
  chatUsage,
  loadAssistantChats,
  newAssistantChatId,
  type AssistantChatHistory,
  type AssistantHistoryTurn,
} from './assistant-history.ts';
import {
  deleteChat as deleteStoredChat,
  importBrowserChats,
  listChats,
  loadChatTurns,
  rememberLocally,
  type AssistantChatSummary,
} from './assistant-chat-store.ts';
import { renderMarkdown } from './markdown.ts';
import { exportHeadline, plotHeadline, prettyColumn, prettyDate } from './humanize.ts';
import {
  applyMention,
  findTrigger,
  rankMentions,
  resolveMentions,
  splitByMentions,
  type MentionItem,
  type MentionTrigger,
} from './mentions.ts';
import { dispatchAssistantNavigation } from './navigation.ts';
import { arriveAssistantCard, renderAssistantCard } from './assistant-cards.ts';
import { buildPageContext } from './page-context.ts';
import './assistant-questions.ts';
import type { QuestionsAnswerDetail } from './assistant-questions.ts';
import { answeredCard, displayValue, questionsOf, questionsStatus } from './questions.ts';
import { lookupLines } from './lookups.ts';
import { assistantAutoPlot } from '../../state/assistant-settings.ts';
import { chiefEngineerOn } from '../../state/assistant-surface.ts';
import { isPageVisible } from '../../state/page-visibility.ts';
import { userMessage } from '../../api/errors.ts';

type Turn = AssistantHistoryTurn;

/** A window in words for the details grid, falling back to whatever is set. */
function humanRange(start?: string | null, end?: string | null): string {
  const from = start ? (prettyDate(start) ?? start) : '…';
  const to = end ? (prettyDate(end) ?? end) : '…';
  return `${from} — ${to}`;
}

/** A proposed Y axis title in words for the details grid; null for auto. */
function axisTitleWords(t: PlotSpecAxisTitle | null | undefined): string | null {
  if (t?.mode === 'off') return 'none';
  if (t?.mode === 'text' && t.text?.trim()) return `"${t.text.trim()}"`;
  if (t?.mode === 'signals' && t.signals?.length) return t.signals.join(', ');
  return null;
}

const RAIL_KEY = 'neptune_assistant_rail_collapsed_v1';

function readRailCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    return false;
  }
}

function writeRailCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(RAIL_KEY, collapsed ? '1' : '0');
  } catch {
    // A browser refusing storage is not worth failing a click over.
  }
}

/**
 * The empty state's suggestions.
 *
 * Phrased as questions a person would actually ask, because they are also the
 * only teaching this screen does: someone who reads four plain sentences types
 * a plain sentence. They fill the composer rather than sending themselves.
 */
function suggestions(ship: string | null): string[] {
  const name = ship ? shipLabel(ship) : 'AIDAbella';
  return [
    `How much fuel did ${name} burn last week?`,
    'Show speed against power for the last month',
    'Compare two ships on fuel this quarter',
    "Export last month's data as a CSV",
  ];
}

@customElement('data-assistant')
export class DataAssistant extends LightElement {
  private store = new StoreController(this, [
    selectedShip,
    assistantTechnical,
    shipMeta,
    allColumns,
    assistantRailWidth,
  ]);

  @state() private turns: Turn[] = [];
  /** The rail: names and dates, never transcripts. */
  @state() private chats: AssistantChatSummary[] = [];
  /** This browser's copy, kept so a reload is instant and an outage invisible. */
  private localChats: AssistantChatHistory[] = [];
  @state() private currentChatId: string | null = null;
  @state() private input = '';
  @state() private busy = false;
  /** What the assistant is doing right now, as the server reports it. */
  @state() private step: string | null = null;
  @state() private applyingPlot = false;
  private plotApplicationDone: Promise<void> = Promise.resolve();
  @state() private error: string | null = null;
  /** null = unknown (not yet checked), true/false = backend reported. */
  @state() private configured: boolean | null = null;
  /** The history rail collapses to a strip; per browser, like the sidebar. */
  @state() private railCollapsed = readRailCollapsed();

  /* ── `@` mentions ─────────────────────────────────────────
     `menu` is what is on screen, `picked` is everything chosen this message -
     which is pruned against the final text on send, so deleting a name deletes
     the claim that it was confirmed. */
  @state() private menu: MentionItem[] = [];
  @state() private menuIndex = 0;
  /** False once the reader has scrolled away from the newest message. */
  @state() private atBottom = true;
  private trigger: MentionTrigger | null = null;
  private picked: MentionItem[] = [];
  /** Undo for dashboard edits made in this page session, by request id. Not
   *  persisted: a snapshot from yesterday would overwrite today's dashboard. */
  private dashboardUndo = new Map<string, () => boolean>();
  @state() private applyingDashboard = false;
  private logObserver: ResizeObserver | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    // The browser copy paints first, because it is already here; the account's
    // list replaces it a moment later and is authoritative.
    this.localChats = loadAssistantChats();
    const latest = this.localChats[0];
    if (latest) {
      this.currentChatId = latest.id;
      this.turns = latest.turns;
    }
    void this.syncChats();
    this.scrollToEnd();
    void this.updateComplete.then(() => {
      if (!this.isConnected || this.logObserver) return;
      // A restored chat is often first rendered in a hidden frame. Wait for
      // its real size, and follow the end as replies or the frame grow.
      this.logObserver = new ResizeObserver(() => {
        if (this.atBottom) this.scrollToEnd();
      });
      for (const el of this.querySelectorAll('.assistant-log, .assistant-log-inner')) {
        this.logObserver.observe(el);
      }
    });
    void getAssistantStatus()
      .then((s) => {
        this.configured = s.configured;
      })
      .catch(() => {
        /* Leave unknown; the chat request surfaces a real error if it fails. */
      });
  }

  override disconnectedCallback(): void {
    this.logObserver?.disconnect();
    this.logObserver = null;
    super.disconnectedCallback();
  }

  private focusComposer(): void {
    const surface = assistantSurface();
    if (surface !== 'drawer' && !(surface === 'full' && assistantTab.value === 'chat')) return;
    this.querySelector<HTMLTextAreaElement>('.assistant-input')?.focus({ preventScroll: true });
  }

  private toggleRail(): void {
    this.railCollapsed = !this.railCollapsed;
    writeRailCollapsed(this.railCollapsed);
  }

  /** Put a suggestion in the composer rather than sending it: the chips are
   *  examples to edit, and sending one unread is how you get an answer to a
   *  question you did not ask. */
  private useSuggestion(text: string): void {
    this.input = text;
    requestAnimationFrame(() => {
      this.querySelector<HTMLTextAreaElement>('.assistant-input')?.focus();
    });
  }

  /** Running totals across the whole conversation. */
  private sessionUsage(): { input: number; output: number; cost: number } {
    return chatUsage(this.turns);
  }

  /**
   * Take the account's list, and hand it this browser's chats the first time.
   *
   * The import is once per person and never fatal: a chat that could not be
   * handed over is still in the browser, and the next load tries again.
   */
  private async syncChats(): Promise<void> {
    try {
      await importBrowserChats();
    } catch {
      /* Reported by the store; the assistant does not stop for it. */
    }
    this.chats = await listChats();
  }

  /** Keep this browser's copy of the open chat. The server stores the turn
   *  itself, as it answers - see `assistant-chat-store.ts`. */
  private persistCurrentChat(): void {
    if (!this.turns.length) return;
    const id = this.currentChatId ?? newAssistantChatId();
    this.currentChatId = id;
    this.localChats = rememberLocally(id, this.turns, this.localChats);
  }

  /**
   * Send the composer's text, or - from a question card - its answers.
   *
   * Answers are an ordinary user turn: their words in `content`, their ids in
   * `answers`. The composer is left as it was, since whatever is half-typed
   * there was not part of the answer.
   */
  private async send(answer?: QuestionsAnswerDetail): Promise<void> {
    const text = answer ? answer.content : this.input.trim();
    if (!text || this.busy) return;
    this.error = null;
    this.busy = true;
    this.step = null;
    if (answer) {
      this.turns = [...this.turns, { role: 'user', content: text, answers: answer.answers }];
    } else {
      // Only the picks still written in the message travel with it: deleting a
      // name has to delete the claim that it was confirmed, or the model would
      // trust context the person removed.
      const mentions = resolveMentions(text, this.picked);
      this.turns = [
        ...this.turns,
        { role: 'user', content: text, ...(mentions.length ? { mentions } : {}) },
      ];
      this.input = '';
      this.picked = [];
      this.menu = [];
      this.trigger = null;
      void this.updateComplete.then(() => {
        const area = this.querySelector<HTMLTextAreaElement>('.assistant-input');
        if (area) this.autoGrow(area);
        this.focusComposer();
      });
    }
    // Sending is a request to look at the end of the conversation. Scrolling up
    // to re-read something and then asking a question used to leave you up
    // there, watching nothing happen.
    this.scrollToEnd();
    this.persistCurrentChat();

    // Send the display history; the backend is stateless. Echo each assistant
    // turn's proposed plot back so the model can edit its own prior proposal.
    const history: AssistantMessage[] = this.turns.map((t) => ({
      role: t.role,
      content: t.content,
      ...(t.mentions?.length ? { mentions: t.mentions } : {}),
      ...(t.plot ? { plot: t.plot } : {}),
      ...(t.export ? { export: t.export } : {}),
      ...(t.dashboardOutcome?.note ? { dashboard_note: t.dashboardOutcome.note } : {}),
      ...(t.card ? { card: t.card } : {}),
      ...(t.answers ? { answers: t.answers } : {}),
    }));

    try {
      // Pass the selected ship as a hint only; the model infers the ship from
      // the query and may choose a different one.
      // Dashboards may exist only in this browser, and the open one may hold
      // unsaved edits, so the page describes them rather than the server
      // looking them up. Null when this account has no dashboards section.
      const [dashboard, catalog] = await Promise.all([
        buildDashboardContext().catch(() => null),
        buildCatalogContext().catch(() => null),
      ]);
      const resp = await runAssistantChat(
        {
          ship_id: selectedShip.value ?? null,
          messages: history,
          ...(dashboard ? { dashboard } : {}),
          ...(catalog ? { catalog } : {}),
          // The chat this turn belongs to. Sent from the first turn, so the
          // server files that one under the same id the rail already shows.
          conversation_id: this.currentChatId,
          ...(chiefEngineerOn() ? { chief_engineer: true } : {}),
          source: activeDataSource.value,
          page: buildPageContext(),
        },
        (text) => {
          this.step = text;
        },
      );
      this.turns = [
        ...this.turns,
        {
          role: 'assistant',
          requestId: resp.assistant_request_id,
          content: resp.reply,
          reasoning: resp.reasoning,
          plot: resp.proposed_plot,
          export: resp.proposed_export,
          ...(resp.dashboard_edit ? { dashboardEdit: resp.dashboard_edit } : {}),
          ...(resp.card ? { card: resp.card } : {}),
          toolCalls: resp.tool_calls,
          plotAttempts: resp.plot_attempts,
          usage: resp.usage,
        },
      ];
      this.persistCurrentChat();
      // The rail's counts and totals come from where the chat is stored, so
      // they are re-read rather than guessed at here.
      void this.syncChats();
      if (resp.card) arriveAssistantCard(resp.card, this);
      if (resp.navigation) dispatchAssistantNavigation(this, resp.navigation);
      else if (resp.dashboard_edit) {
        await this.applyDashboard(this.turns.length - 1, resp.dashboard_edit, resp.assistant_request_id);
      }
      else if (resp.proposed_plot && assistantAutoPlot.value && isPageVisible('plot')) {
        await this.apply(this.turns.length - 1, resp.proposed_plot, true);
      }
    } catch (e) {
      const msg = userMessage(e);
      this.error = msg;
    } finally {
      this.busy = false;
      this.scrollToEnd();
    }
  }

  private newChat(): void {
    if (this.busy) return;
    this.currentChatId = null;
    this.turns = [];
    this.input = '';
    this.error = null;
  }

  /** Start over from outside the panel - the drawer's own New button, which
   *  has no rail to put one in. Ignored mid-reply, as the rail's button is. */
  startNewChat(): void {
    this.newChat();
  }

  /** Open a chat by id, for the History page - which knows the id and not the
   *  panel's own copy of the list. Re-reads first, since History may have
   *  deleted something since this panel last looked. */
  openChatById(id: string): void {
    void this.openChat(id);
  }

  /** Open a chat, fetching its transcript from wherever it is stored. */
  private async openChat(id: string): Promise<void> {
    if (this.busy) return;
    this.currentChatId = id;
    this.input = '';
    this.error = null;
    // Show what this browser has while the stored transcript arrives; for a
    // chat from another machine there is nothing local, and the log is empty
    // for the moment it takes.
    this.turns = this.localChats.find((chat) => chat.id === id)?.turns ?? [];
    this.scrollToEnd();
    const stored = await loadChatTurns(id);
    if (stored && this.currentChatId === id) {
      this.turns = stored;
      this.localChats = rememberLocally(id, stored, this.localChats);
      this.scrollToEnd();
    }
  }

  private deleteChat(id: string, event: Event): void {
    event.stopPropagation();
    if (this.busy) return;
    this.chats = this.chats.filter((chat) => chat.id !== id);
    this.localChats = this.localChats.filter((chat) => chat.id !== id);
    if (this.currentChatId === id) this.newChat();
    void deleteStoredChat(id);
  }

  private async apply(index: number, spec: PlotSpec, automatic = false): Promise<void> {
    if (this.applyingPlot) {
      if (!automatic) return;
      await this.plotApplicationDone;
      if (!assistantAutoPlot.value) return;
    }
    const focused = document.activeElement;
    const returnToComposer = !automatic && focused !== null && this.contains(focused);
    this.applyingPlot = true;
    let finishApplication!: () => void;
    this.plotApplicationDone = new Promise((resolve) => { finishApplication = resolve; });
    const turn = this.turns[index];
    const tracking =
      !automatic && turn?.requestId && !turn.plotApplyClicked
        ? recordAssistantPlotApplyClicked({
            assistant_request_id: turn.requestId,
            ship_id: spec.ship_id,
            plot_type: spec.plot_type,
          }).then(
            () => true,
            () => false,
          )
        : null;

    try {
      await applyProposedPlot(spec);
      this.turns = this.turns.map((item, i) =>
        i === index ? { ...item, plotApplied: true } : item,
      );
      this.persistCurrentChat();
    } catch (e) {
      this.error = userMessage(e);
    } finally {
      this.applyingPlot = false;
      finishApplication();
      // Applying disables the clicked card button. Return to typing without
      // taking focus back if the person moved to another control meanwhile.
      if (
        returnToComposer &&
        (document.activeElement === focused || document.activeElement === document.body)
      ) {
        void this.updateComplete.then(() => this.focusComposer());
      }
    }

    if (tracking && (await tracking)) {
      const currentTurn = this.turns[index];
      if (currentTurn && !currentTurn.plotApplyClicked) {
        this.turns = this.turns.map((item, i) =>
          i === index ? { ...item, plotApplyClicked: true } : item,
        );
        this.persistCurrentChat();
      }
    }
  }

  /**
   * Apply a dashboard edit the moment it arrives.
   *
   * No card to click first: the person asked for the change, and it lands as
   * unsaved edits they keep with Save - the review step is the dashboard
   * itself, with Undo here and Discard there.
   */
  private async applyDashboard(index: number, edit: DashboardEdit, requestId: string): Promise<void> {
    this.applyingDashboard = true;
    try {
      const outcome = await applyDashboardEdit(edit, () =>
        dispatchAssistantNavigation(this, { target: 'dashboards' }),
      );
      if (outcome.undo) this.dashboardUndo.set(requestId, outcome.undo);
      this.turns = this.turns.map((item, i) =>
        i === index
          ? {
              ...item,
              dashboardOutcome: {
                dashboardId: outcome.dashboardId,
                dashboardName: outcome.dashboardName,
                created: outcome.created,
                done: outcome.done,
                problems: outcome.problems,
                note: outcomeNote(outcome),
              },
            }
          : item,
      );
      this.persistCurrentChat();
    } catch (e) {
      this.error = userMessage(e);
    } finally {
      this.applyingDashboard = false;
    }
  }

  private undoDashboard(index: number): void {
    const turn = this.turns[index];
    const undo = turn?.requestId ? this.dashboardUndo.get(turn.requestId) : undefined;
    const outcome = turn?.dashboardOutcome;
    if (!turn?.requestId || !undo || !outcome) return;
    if (!undo()) {
      this.error = `Open “${outcome.dashboardName ?? 'the dashboard'}” to undo this.`;
      return;
    }
    this.dashboardUndo.delete(turn.requestId);
    const note = `${outcome.note}. The person then pressed Undo: none of it is on the dashboard any more.`;
    this.turns = this.turns.map((item, i) =>
      i === index ? { ...item, dashboardOutcome: { ...outcome, undone: true, note } } : item,
    );
    this.persistCurrentChat();
  }

  private openOutcomeDashboard(index: number): void {
    const id = this.turns[index]?.dashboardOutcome?.dashboardId;
    if (!id) return;
    void applyDashboardEdit({ dashboard_id: id }, () =>
      dispatchAssistantNavigation(this, { target: 'dashboards' }),
    ).then((outcome) => {
      if (!outcome.ok) this.error = outcome.problems.join(' ');
    });
  }

  private async applyExport(index: number, spec: ExportSpec): Promise<void> {
    try {
      await applyProposedExport(spec);
      this.turns = this.turns.map((item, i) =>
        i === index ? { ...item, exportApplyClicked: true } : item,
      );
      this.persistCurrentChat();
    } catch (e) {
      this.error = userMessage(e);
    }
  }

  private async markBad(index: number): Promise<void> {
    const turn = this.turns[index];
    if (turn?.role !== 'assistant' || !turn.requestId) return;
    if (turn.feedbackState === 'bad' || turn.feedbackState === 'unsaved') return;

    let askedFor = '';
    for (let i = index - 1; i >= 0; i -= 1) {
      const candidate = this.turns[i];
      if (candidate?.role === 'user') {
        askedFor = candidate.content;
        break;
      }
    }

    const setState = (feedbackState: NonNullable<Turn['feedbackState']>) => {
      this.turns = this.turns.map((t, i) => (i === index ? { ...t, feedbackState } : t));
    };
    setState('sending');
    try {
      const { recorded } = await sendAssistantFeedback({
        assistant_request_id: turn.requestId,
        feedback: 'bad',
        user_message: askedFor,
        assistant_answer: turn.content,
        assistant_reasoning: turn.reasoning ?? null,
        proposed_plot: turn.plot ?? null,
        proposed_export: turn.export ?? null,
      });
      // Thanks only for what was kept. Without stored chats there is nothing
      // to attach it to, and saying "noted" would be untrue.
      setState(recorded ? 'bad' : 'unsaved');
      this.persistCurrentChat();
    } catch (e) {
      setState('idle');
      this.error = userMessage(e);
    }
  }

  private scrollToEnd(): void {
    this.atBottom = true;
    void this.updateComplete.then(() => {
      requestAnimationFrame(() => {
        const log = this.querySelector<HTMLElement>('.assistant-log');
        if (!this.isConnected || !log?.clientHeight) return;
        log.scrollTop = log.scrollHeight;
      });
    });
  }

  /** Within a line or so of the end counts as "at the end". */
  private onLogScroll(e: Event): void {
    const log = e.target as HTMLElement;
    if (!log.clientHeight) return;
    this.atBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 32;
  }

  /**
   * What `@` can offer.
   *
   * Ships are always available - all 56 are in memory from boot. Signals are
   * whatever columns the app currently has loaded, which is the selected ship's
   * schema; with no ship loaded the menu is ships only, and naming one is
   * exactly how you get the signals.
   */
  private mentionPool(): MentionItem[] {
    const ships: MentionItem[] = Object.entries(shipMeta.value).map(([id, meta]) => ({
      kind: 'ship',
      id,
      label: meta?.name || id,
      hint: meta?.brand || id,
    }));
    // `exactOptionalPropertyTypes` is on, so an absent hint is an absent key
    // rather than an explicit undefined.
    const ship = selectedShip.value;
    const shipHint = ship ? { hint: shipLabel(ship) } : {};
    const signals: MentionItem[] = allColumns.value.map((col) => ({
      kind: 'signal',
      id: col.name,
      label: col.name,
      ...shipHint,
    }));
    return [...ships, ...signals];
  }

  /** Re-read the `@…` under the caret and refresh the menu under it. */
  private refreshMenu(area: HTMLTextAreaElement): void {
    const caret = area.selectionStart ?? area.value.length;
    this.trigger = findTrigger(area.value, caret);
    if (!this.trigger) {
      this.menu = [];
      return;
    }
    const hits = rankMentions(this.trigger.query, this.mentionPool());
    this.menu = hits;
    this.menuIndex = 0;
  }

  /**
   * Tagged names, painted blue.
   *
   * The composer is a `<textarea>`, which cannot colour part of its own text -
   * so the box holds a layer behind it that renders the same string with the
   * tagged names marked, and the textarea's own glyphs are made transparent
   * over the top of it. The caret, selection and every native editing behaviour
   * are still the textarea's; only the paint comes from underneath.
   *
   * The same function paints the message once it is sent, so what you tagged
   * looks the same before and after.
   */
  private renderTagged(text: string, mentions: ReadonlyArray<{ kind: 'ship' | 'signal'; label: string }>): TemplateResult {
    return html`${splitByMentions(text, mentions).map((seg) =>
      seg.kind
        ? html`<span class="assistant-tagged assistant-tagged-${seg.kind}">${seg.text}</span>`
        : html`${seg.text}`,
    )}`;
  }

  /** Keep the layer under the textarea in step with the textarea's own scroll. */
  private syncHighlightScroll(area: HTMLTextAreaElement): void {
    const layer = this.querySelector<HTMLElement>('.assistant-highlight');
    if (layer) layer.scrollTop = area.scrollTop;
  }

  /** Grow the pill to fit what is being typed, up to a limit, then scroll. */
  private autoGrow(area: HTMLTextAreaElement): void {
    area.style.height = 'auto';
    area.style.height = `${Math.min(area.scrollHeight, 180)}px`;
  }

  private onComposerInput(e: Event): void {
    const area = e.target as HTMLTextAreaElement;
    this.input = area.value;
    this.autoGrow(area);
    this.refreshMenu(area);
    this.syncHighlightScroll(area);
  }

  /** Picking writes the exact name into the message and remembers the id. */
  private pickMention(item: MentionItem): void {
    const area = this.querySelector<HTMLTextAreaElement>('.assistant-input');
    if (!area || !this.trigger) return;
    const caret = area.selectionStart ?? area.value.length;
    const next = applyMention(area.value, this.trigger, item, caret);
    this.input = next.text;
    this.picked = [...this.picked, item];
    this.menu = [];
    this.trigger = null;
    // Lit writes `.value` on the next render; the caret has to follow it there
    // or it lands at the end of the box rather than after the name.
    void this.updateComplete.then(() => {
      area.focus();
      area.setSelectionRange(next.caret, next.caret);
    });
  }

  private onKeydown(e: KeyboardEvent): void {
    const area = e.target as HTMLTextAreaElement;
    // While the menu is open it owns the keys a menu owns - and only those.
    if (this.menu.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.menuIndex = (this.menuIndex + 1) % this.menu.length;
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.menuIndex = (this.menuIndex - 1 + this.menu.length) % this.menu.length;
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const item = this.menu[this.menuIndex];
        if (item) this.pickMention(item);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this.menu = [];
        this.trigger = null;
        return;
      }
    }
    // Enter sends; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void this.send();
      return;
    }
    // Arrow keys and clicks move the caret, which can move it out of an `@…`
    // or into one. Re-read after the browser has applied the key.
    if (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End') {
      requestAnimationFrame(() => this.refreshMenu(area));
    }
  }

  /** Every populated setting that applies to this plot type. */
  private specRows(spec: PlotSpec): Array<{ k: string; v: TemplateResult | string }> {
    const rows: Array<{ k: string; v: TemplateResult | string }> = [];
    const add = (k: string, v: TemplateResult | string | null | undefined) => {
      if (v != null && v !== '') rows.push({ k, v });
    };
    const list = (values: string[] | undefined) => (values?.length ? values.join(', ') : null);

    // Named explicitly: the card header shows only the primary ship, and
    // applying a proposal that quietly plotted three would be a surprise.
    const compare = (spec.compare_ship_ids ?? []).filter((s) => s && s !== spec.ship_id);
    if (compare.length) {
      add('ships', [spec.ship_id, ...compare].map(shipLabel).join(', '));
      add('layout', spec.plot_type === 'gauge'
        ? ({ ship: 'by ship', sensor: 'by sensor', matrix: 'matrix' } as const)[spec.gauge_layout ?? 'ship']
        : spec.view_mode === 'grid' || spec.plot_type === 'number' || spec.plot_type === 'sankey'
          ? 'grid - one panel per ship' : 'overlay - one chart');
    }

    if (spec.plot_type === 'number') {
      if (spec.number_mode === 'formula') {
        add('calculation', spec.number_expression ? html`<code>${spec.number_expression}</code>` : null);
      } else {
        add('signal', spec.number_column);
        add('summary', spec.number_agg || 'latest');
      }
      add('label', spec.number_label);
      add('unit', spec.number_unit);
      add('decimal places', String(spec.number_decimals ?? 2));
    } else if (spec.plot_type === 'gauge') {
      add('dials', html`${(spec.gauge_dials ?? []).map((d) => {
        const what = d.mode === 'formula' ? d.expression ?? '' : `${d.agg || 'latest'} ${d.column ?? ''}`;
        const scale = d.min != null || d.max != null ? ` · ${d.min ?? 'auto'}–${d.max ?? 'auto'}` : '';
        const zones = (d.zones ?? []).map((z) => `${z.color} ${z.start ?? 'min'}–${z.end ?? 'max'}`).join(', ');
        return html`<div>${d.label || what}${d.unit ? ` (${d.unit})` : ''}: <code>${what}</code>${scale}${zones ? ` · ${zones}` : ''}</div>`;
      })}`);
    } else if (spec.plot_type === 'sankey') {
      // Column by column, each node with what it reads and where it flows.
      const nodes = [...(spec.sankey_nodes ?? [])].sort((a, b) => (a.col ?? 0) - (b.col ?? 0));
      const title = (id: string) => {
        const n = nodes.find((x) => x.id === id);
        return n ? n.label || n.column || n.expression || id : id;
      };
      add('nodes', html`${nodes.map((n) => {
        const what = n.kind === 'sum' ? 'sum of inputs' : n.kind === 'rest' ? 'remainder'
          : n.kind === 'formula' ? n.expression ?? '' : `${n.agg || 'avg'} ${n.column ?? ''}`;
        const to = (n.to ?? []).map(title).join(', ');
        return html`<div>${(n.col ?? 0) + 1}. ${n.label || what}: <code>${what}</code>${to ? ` → ${to}` : ''}</div>`;
      })}`);
      add('unit', spec.sankey_unit);
      add('decimal places', String(spec.sankey_decimals ?? 0));
      if (spec.sankey_motion === false) add('moving dashes', 'off');
    } else if (spec.plot_type === 'histogram') {
      add('signals', list(spec.hist_columns));
      add('buckets', spec.hist_bins != null ? String(spec.hist_bins) : null);
    } else if (spec.plot_type === 'correlation') {
      add('signals', list(spec.corr_columns));
      add('measure', spec.corr_method === 'spearman' ? 'Spearman (rank)' : 'Pearson (linear)');
      if (spec.corr_order === 'cluster') add('arranged', 'so related signals sit together');
    } else if (spec.plot_type === 'pie') {
      if (spec.pie_mode === 'bands') {
        add('signal', spec.pie_band_column ?? null);
        add('bands', spec.pie_band_edges?.length ? spec.pie_band_edges.join(', ') : 'from the data');
      } else {
        add('slices', list(spec.pie_columns));
        add('slice size', spec.pie_agg === 'sum' ? 'sum' : 'mean');
      }
    } else if (spec.plot_type === 'box') {
      if (spec.box_mode === 'groups') {
        add('signal', spec.box_column ?? null);
        add('split by', spec.box_group_column ?? null);
      } else {
        add('boxes', list(spec.box_columns));
      }
    } else if (spec.plot_type === 'table' && spec.table_mode === 'readings') {
      add('readings of', list(spec.table_columns));
    } else if (spec.plot_type === 'table') {
      const groups = spec.table_groups ?? [];
      add('rows', groups.length ? groups.map(groupLabel).join(' × ') : 'one summary row');
      add('columns', html`${(spec.table_measures ?? []).map(
        (m) => html`<span class="assistant-formula">${measureLabel(m)}</span>`,
      )}`);
    } else if (spec.plot_type === 'map') {
      add('latitude', spec.latitude_column);
      add('longitude', spec.longitude_column);
      add('coloured by', spec.color_by);
    } else {
      add('across', prettyColumn(spec.x_column ?? 'timestampNDC'));
      add('left axis', list(spec.y_left));
      add('right axis', list(spec.y_right));
      add('left title', axisTitleWords(spec.y_title));
      add('right title', axisTitleWords(spec.y_right_title));
      if (spec.plot_type === 'scatter') add('coloured by', spec.color_by);
    }
    add('only where', spec.filter_expr ? html`<code>${spec.filter_expr}</code>` : null);
    add('slicers', spec.slicers?.length ? spec.slicers.map((s) => prettyColumn(s.column)).join(', ') : null);

    if (spec.derived?.length) {
      add(
        'calculated',
        html`${spec.derived.map(
            (d) => html`<span class="assistant-formula">${d.name} = <code>${d.expr}</code></span>`,
          )}`,
      );
    }

    if (spec.fits?.length) {
      add(
        'trend lines',
        html`${spec.fits.map((f) => {
          const params = Object.entries(f.params ?? {})
            .map(([k, val]) => `${k}=${val}`)
            .join(', ');
          return html`<span class="assistant-fit"
            >${f.series} · ${f.kind}${params ? ` (${params})` : ''}${f.color
              ? ` · ${f.color}`
              : ''}</span
          >`;
        })}`,
      );
    }

    const colorKeys = Object.keys(spec.custom_colors ?? {});
    if (colorKeys.length) {
      add(
        'colours',
        html`${colorKeys.map((c) => {
          const hex = spec.custom_colors?.[c] ?? '';
          return html`<span class="assistant-swatch"
            ><span class="assistant-swatch-dot" style="background:${hex}"></span>${c} ·
            <code>${hex}</code></span
          >`;
        })}`,
      );
    }

    // A comparison is several date ranges, not one: name each by its letter.
    const periods = spec.plot_type === 'line' || spec.plot_type === 'scatter' ? spec.compare_periods ?? [] : [];
    if (periods.length && !compare.length) {
      const label = (letter: string, name: string | null | undefined) =>
        name?.trim() ? `${letter} · ${name.trim()}` : letter;
      add(label('A', spec.reference_label || 'Reference'), humanRange(spec.start_date, spec.end_date));
      periods.forEach((period, i) =>
        add(label(String.fromCharCode(66 + i), period.label), humanRange(period.start_date, period.end_date)),
      );
      add('time axis', spec.period_time_axis === 'utc' ? 'calendar dates (UTC)' : 'elapsed from each start');
    } else if (spec.start_date || spec.end_date) {
      add('dates', humanRange(spec.start_date, spec.end_date));
    }
    return rows;
  }

  private renderPlotCard(
    spec: PlotSpec,
    turnIndex: number,
    alreadyApplied = false,
  ): TemplateResult {
    const rows = this.specRows(spec);
    return html`
      <div class="assistant-plotcard">
        <div class="assistant-plotcard-head">
          <span class="assistant-plotcard-title">${plotHeadline(spec)}</span>
          <button
            class="assistant-send"
            ?disabled=${this.applyingPlot}
            @click=${() => void this.apply(turnIndex, spec)}
          >
            ${alreadyApplied ? 'Show me again →' : 'Show me →'}
          </button>
        </div>
        <span class="assistant-plotcard-type">${spec.plot_type}</span>
        ${spec.explanation
          ? html`<p class="assistant-plotcard-why">${spec.explanation}</p>`
          : nothing}
        ${this.renderSpecDetails(rows)}
      </div>
    `;
  }

  /**
   * Every parameter, one disclosure away.
   *
   * The headline is for recognising the proposal; this is for checking it - the
   * exact signal ids, the filter expression, the dates - before applying
   * something to the plot you are working on. It is closed by default and the
   * whole list is still here, so nothing is hidden, only quiet.
   */
  private renderSpecDetails(
    rows: Array<{ k: string; v: TemplateResult | string }>,
  ): TemplateResult | typeof nothing {
    if (!rows.length) return nothing;
    return html`<details class="assistant-specs">
      <summary>the settings behind this</summary>
      <dl class="assistant-plotcard-params">
        ${rows.map(
          (r) => html`<dt>${r.k}</dt>
            <dd>${r.v}</dd>`,
        )}
      </dl>
    </details>`;
  }

  private renderExportCard(
    spec: ExportSpec,
    turnIndex: number,
    alreadyClicked = false,
  ): TemplateResult {
    const rows: Array<{ k: string; v: string | TemplateResult }> = [
      { k: 'signals', v: spec.columns.length ? spec.columns.join(', ') : 'every signal' },
      { k: 'file', v: (spec.format || 'csv').toUpperCase() },
    ];
    if (spec.start_date || spec.end_date) {
      rows.push({ k: 'dates', v: humanRange(spec.start_date, spec.end_date) });
    }
    if (spec.filter_expr) rows.push({ k: 'only where', v: html`<code>${spec.filter_expr}</code>` });
    if (spec.frequency) rows.push({ k: 'averaged over', v: spec.frequency });
    if (spec.fill_method && spec.fill_method !== 'none') {
      rows.push({ k: 'gaps', v: spec.fill_method });
    }
    return html`
      <div class="assistant-plotcard">
        <div class="assistant-plotcard-head">
          <span class="assistant-plotcard-title">${exportHeadline(spec)}</span>
          <button class="assistant-send" @click=${() => void this.applyExport(turnIndex, spec)}>
            ${alreadyClicked ? 'Export it again →' : 'Export it →'}
          </button>
        </div>
        <span class="assistant-plotcard-type">data export</span>
        ${spec.explanation ? html`<p class="assistant-plotcard-why">${spec.explanation}</p>` : nothing}
        ${this.renderSpecDetails(rows)}
      </div>
    `;
  }

  private renderToolCalls(t: Turn): TemplateResult | typeof nothing {
    if (!t.toolCalls?.length) return nothing;
    // Which tools ran, with what, is plumbing. It is the first thing someone
    // debugging a proposal wants and the last thing anyone else does.
    if (!assistantTechnical.value) return nothing;
    const n = t.toolCalls.length;
    return html`<details class="assistant-tools">
      <summary>checked ${n} thing${n === 1 ? '' : 's'}</summary>
      <div class="assistant-tool-list">
        ${t.toolCalls.map((call) => {
          const name = typeof call.name === 'string' ? call.name : 'tool';
          const status = typeof call.status === 'string' ? call.status : '';
          const input = call.input;
          const errors = Array.isArray(call.validation_errors)
            ? call.validation_errors.join('\n')
            : null;
          const result = typeof call.result === 'string' ? call.result : null;
          return html`<details class="assistant-tool">
            <summary>
              <code>${name}</code>${status
                ? html`<span class="assistant-tool-status assistant-tool-status-${status}"
                    >${status}</span
                  >`
                : nothing}
            </summary>
            <div class="assistant-tool-body">
              ${input !== undefined
                ? html`<div><strong>Input</strong><pre>${JSON.stringify(input, null, 2)}</pre></div>`
                : nothing}
              ${errors ? html`<div><strong>Validation</strong><pre>${errors}</pre></div>` : nothing}
              ${result ? html`<div><strong>Result</strong><pre>${result}</pre></div>` : nothing}
            </div>
          </details>`;
        })}
      </div>
    </details>`;
  }

  private historyDate(iso: string): string {
    const date = new Date(iso);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  /**
   * Drag the rail's edge, the way Explore's sidebar divider drags.
   *
   * The rail is anchored left, so its width is simply where the pointer is.
   */
  private onRailGripDown(e: PointerEvent): void {
    const grip = e.currentTarget as HTMLElement;
    e.preventDefault();
    grip.setPointerCapture(e.pointerId);
    document.body.classList.add('resizing-panel');
    const move = (ev: PointerEvent): void => {
      const left = this.getBoundingClientRect().left;
      setAssistantRailWidth(ev.clientX - left);
    };
    const up = (): void => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      document.body.classList.remove('resizing-panel');
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  }

  private onRailGripKey(e: KeyboardEvent): void {
    const step = e.shiftKey ? 64 : 16;
    if (e.key === 'ArrowRight') setAssistantRailWidth(assistantRailWidth.value + step);
    else if (e.key === 'ArrowLeft') setAssistantRailWidth(assistantRailWidth.value - step);
    else return;
    e.preventDefault();
  }

  /** The rail's own edge handle - the same hairline-with-a-grab-mark the drawer
   *  uses, so a resizable edge looks the same wherever one is. */
  private renderRailGrip(): TemplateResult | typeof nothing {
    if (this.railCollapsed) return nothing;
    return html`<div
      class="assistant-grip assistant-rail-grip"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the chat list"
      aria-valuenow=${assistantRailWidth.value}
      aria-valuemin=${RAIL_MIN}
      aria-valuemax=${railMax()}
      tabindex="0"
      title="Drag to resize"
      @pointerdown=${(e: PointerEvent) => this.onRailGripDown(e)}
      @keydown=${(e: KeyboardEvent) => this.onRailGripKey(e)}
    >
      <span class="assistant-grip-line" aria-hidden="true"></span>
    </div>`;
  }

  /** The collapse chevron, drawn exactly as the Explore sidebar's is - same
   *  class, same path - because two controls that do the same thing in the same
   *  application should not be two different shapes. */
  private renderRailToggle(): TemplateResult {
    const collapsed = this.railCollapsed;
    return html`
      <button
        class="sidebar-toggle assistant-rail-toggle"
        title=${collapsed ? 'Show chats' : 'Hide chats'}
        aria-label="Toggle the chat list"
        aria-expanded=${!collapsed}
        @click=${() => this.toggleRail()}
      >
        <svg
          class="sidebar-toggle-icon"
          width="10"
          height="16"
          viewBox="0 0 10 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d=${collapsed ? 'M3 2.5 8 8l-5 5.5' : 'M7 2.5 2 8l5 5.5'}
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    `;
  }

  /**
   * The chat rail.
   *
   * Built out of the same parts as Explore's left panel rather than a slab of
   * its own: a transparent column on the page background, a hairline edge, a
   * `.card` block under a mono section label, and the app's own chevron on the
   * boundary. It was the one surface in the application that had invented its
   * own furniture - a raised panel, a pill button, rounded rows - which is why
   * it read as borrowed from somewhere else.
   *
   * There is no `01` on the heading. The numbers in Explore mean something -
   * Query, then Plot, then Filters, in that order - and a lone section numbered
   * 01 would be decoration wearing the costume of structure.
   */
  private renderRail(): TemplateResult {
    if (this.railCollapsed) {
      return html`<aside class="assistant-rail assistant-rail-collapsed" aria-hidden="true"></aside>`;
    }
    return html`<aside class="assistant-rail" aria-label="Your conversations">
      <div class="card">
        <h2>
          Chats
          <button
            class="btn btn-secondary assistant-rail-new"
            ?disabled=${this.busy}
            @click=${() => this.newChat()}
          >
            New chat
          </button>
        </h2>

        <div class="assistant-rail-list">
          ${this.chats.length
            ? this.chats.map(
                (chat) => html`<div
                  class="assistant-rail-row ${chat.id === this.currentChatId ? 'active' : ''}"
                >
                  <button class="assistant-rail-open" @click=${() => void this.openChat(chat.id)}>
                    <span class="assistant-rail-title">${chat.title}</span>
                    <!-- When, and nothing else. What a chat cost is not how you
                         recognise it, and it was the third dollar figure on a
                         screen that had no business keeping a running bill. -->
                    <span class="assistant-rail-when">${this.historyDate(chat.updatedAt)}</span>
                  </button>
                  <button
                    class="assistant-rail-delete"
                    title="Delete this chat"
                    aria-label="Delete ${chat.title}"
                    @click=${(event: Event) => this.deleteChat(chat.id, event)}
                  >
                    ×
                  </button>
                </div>`,
              )
            : html`<p class="assistant-rail-empty">Your chats will appear here.</p>`}
        </div>

        <!-- The door to History from inside a conversation: the rail holds the
             recent ones, the page holds all of them. -->
        <button class="assistant-rail-all" @click=${() => setAssistantTab('history')}>
          All chats →
        </button>
      </div>
    </aside>`;
  }

  /**
   * What a dashboard edit did - a receipt, not a proposal: it has already
   * happened. Undo sits where Show me sits on a plot card; opening the
   * dashboard again is the quieter link beside it.
   */
  private renderDashboardCard(t: Turn, index: number): TemplateResult {
    const o = t.dashboardOutcome!;
    const canUndo = !o.undone && !!t.requestId && this.dashboardUndo.has(t.requestId);
    const changed = o.done.length > 0;
    const headline = o.dashboardName
      ? `${o.created ? 'New dashboard' : 'Dashboard'} · ${o.dashboardName}`
      : 'Dashboard';
    return html`
      <div class="assistant-plotcard assistant-dashcard">
        <div class="assistant-plotcard-head">
          <span class="assistant-plotcard-title">${headline}</span>
          ${canUndo
            ? html`<button class="assistant-send" @click=${() => this.undoDashboard(index)}>Undo</button>`
            : o.dashboardId
              ? html`<button
                  class="assistant-send"
                  ?disabled=${this.applyingDashboard}
                  @click=${() => this.openOutcomeDashboard(index)}
                >
                  Open →
                </button>`
              : nothing}
        </div>
        <span class="assistant-plotcard-type">
          ${o.undone ? 'undone' : changed ? 'unsaved changes · Save on the dashboard to keep them' : 'opened'}
        </span>
        ${changed
          ? html`<ul class="assistant-dashcard-list ${o.undone ? 'is-undone' : ''}">
              ${o.done.map((line) => html`<li>${line}</li>`)}
            </ul>`
          : nothing}
        ${o.problems.length
          ? html`<ul class="assistant-dashcard-list assistant-dashcard-problems">
              ${o.problems.map((line) => html`<li>${line}</li>`)}
            </ul>`
          : nothing}
      </div>
    `;
  }

  /**
   * A user turn that answered a question card: each answer against its
   * question, marked where it is not what the Assistant suggested. Without the
   * card to read labels from (a chat trimmed in this browser), the words the
   * turn was sent with say the same thing.
   */
  private renderAnswers(index: number): TemplateResult {
    const t = this.turns[index]!;
    const card = answeredCard(this.turns, index);
    if (!card || !t.answers) return html`<div class="assistant-bubble">${t.content}</div>`;
    return html`<div class="assistant-bubble aq-answers">
      <dl>
        ${card.questions.map((q) => {
          const value = t.answers!.values[q.id];
          const changed = q.suggested !== undefined && q.suggested !== null &&
            String(Array.isArray(value) ? [...value].sort() : value) !==
            String(Array.isArray(q.suggested) ? [...q.suggested].sort() : q.suggested);
          return html`<dt>${q.label}</dt><dd class=${q.kind === 'signal' ? 'is-signal' : ''}
            >${displayValue(q, value)}${changed ? html`<span class="aq-changed">changed</span>` : nothing}</dd>`;
        })}
      </dl>
    </div>`;
  }

  /**
   * What the Assistant looked up on the way to its answer, for everyone - one
   * quiet line, opened on demand. The tool-by-tool detail stays behind the
   * technical switch.
   */
  private renderLookups(t: Turn): TemplateResult | typeof nothing {
    if (assistantTechnical.value || !t.toolCalls?.length) return nothing;
    const lines = lookupLines(t.toolCalls);
    if (!lines.length) return nothing;
    return html`<details class="assistant-tools aq-lookups">
      <summary>Looked up ${lines.length} thing${lines.length === 1 ? '' : 's'}</summary>
      <ul>${lines.map((line) => html`<li>${line}</li>`)}</ul>
    </details>`;
  }

  private renderTurn(t: Turn, index: number): TemplateResult {
    // Assistant replies are Markdown (rendered to sanitized HTML); user turns
    // are shown verbatim (the bubble CSS preserves their whitespace).
    const body =
      t.role === 'assistant'
        // No whitespace inside either element: the bubble preserves whitespace
        // so that a typed line break survives, which means the template's own
        // newline and indentation would render as a blank line above and below
        // every message - a one-line question in a three-line box.
        // prettier-ignore
        ? html`<div class="assistant-bubble assistant-md">${unsafeHTML(renderMarkdown(t.content))}</div>`
        : t.answers
          ? this.renderAnswers(index)
          // prettier-ignore
          : html`<div class="assistant-bubble">${t.mentions?.length ? this.renderTagged(t.content, t.mentions) : t.content}</div>`;
    const questions = questionsOf(t.card);
    return html`
      <div class="assistant-turn assistant-turn-${t.role}">
        ${body}
        ${t.reasoning && assistantTechnical.value
          ? html`<details class="assistant-reasoning">
              <summary>how it worked this out</summary>
              <div>${t.reasoning}</div>
            </details>`
          : nothing}
        ${this.renderToolCalls(t)}
        ${this.renderLookups(t)}
        ${t.plot ? this.renderPlotCard(t.plot, index, t.plotApplied || t.plotApplyClicked) : nothing}
        ${t.export ? this.renderExportCard(t.export, index, t.exportApplyClicked) : nothing}
        ${t.dashboardOutcome ? this.renderDashboardCard(t, index) : nothing}
        ${questions
          ? html`<assistant-questions
              .payload=${questions}
              status=${questionsStatus(this.turns, index, this.busy)}
              @questions-answer=${(e: CustomEvent<QuestionsAnswerDetail>) => void this.send(e.detail)}
            ></assistant-questions>`
          : t.card
            ? renderAssistantCard(t.card)
            : nothing}
        ${t.role === 'assistant'
          ? html`<div class="assistant-turn-meta">
              ${t.requestId
                ? html`<button
                    class="assistant-feedback"
                    aria-label="Tell us this answer was not what you asked for"
                    title=${t.feedbackState === 'unsaved'
                      ? 'This could not be saved: feedback is kept only for chats stored in your history'
                      : 'Tell us this was not what you asked for'}
                    ?disabled=${t.feedbackState === 'sending' || t.feedbackState === 'bad' || t.feedbackState === 'unsaved'}
                    @click=${() => void this.markBad(index)}
                  >
                    ${t.feedbackState === 'bad'
                      ? '✓ Thanks — noted'
                      : t.feedbackState === 'unsaved'
                        ? 'Could not be saved'
                      : t.feedbackState === 'sending'
                        ? 'Sending…'
                        : 'Not what I asked for'}
                  </button>`
                : nothing}
            </div>`
          : nothing}
      </div>
    `;
  }

  /**
   * What a fresh conversation says.
   *
   * It used to be two paragraphs and a privacy notice - three blocks of prose
   * between someone and their first question, none of which answered "what do I
   * type". Four examples answer it, and clicking one puts it in the composer to
   * edit rather than sending it.
   */
  private renderEmpty(ship: string | null): TemplateResult {
    return html`
      <div class="assistant-empty">
        <p class="assistant-empty-title">What do you want to look at?</p>
        <div class="assistant-suggestions">
          ${suggestions(ship).map(
            (text) =>
              html`<button class="assistant-suggestion" @click=${() => this.useSuggestion(text)}>
                ${text}
              </button>`,
          )}
        </div>
      </div>
    `;
  }

  /**
   * The suggestion box.
   *
   * It sits above the composer rather than below it, because the composer is
   * already at the bottom of the window and a menu that opens downwards would
   * open off the screen. Anchored to the box, not the caret: a menu that walks
   * left and right as you type is harder to read than one that stays put.
   */
  private renderMentionMenu(): TemplateResult | typeof nothing {
    if (!this.menu.length) return nothing;
    return html`
      <div class="assistant-mentions" id="assistant-mentions" role="listbox">
        ${this.menu.map(
          (item, i) => html`<button
            class="assistant-mention ${i === this.menuIndex ? 'active' : ''}"
            role="option"
            aria-selected=${i === this.menuIndex}
            @mousedown=${(e: Event) => e.preventDefault()}
            @click=${() => this.pickMention(item)}
          >
            <span class="assistant-mention-kind">${item.kind === 'ship' ? 'Ship' : 'Signal'}</span>
            <span class="assistant-mention-label">${item.label}</span>
            ${item.hint ? html`<span class="assistant-mention-hint">${item.hint}</span>` : nothing}
          </button>`,
        )}
      </div>
    `;
  }

  /** Let a click on the menu land before the blur closes it. */
  private closeMenuSoon(): void {
    setTimeout(() => {
      this.menu = [];
      this.trigger = null;
    }, 120);
  }

  override render(): TemplateResult {
    const ship = selectedShip.value;
    const session = this.sessionUsage();
    const hasUsage = this.turns.some((t) => t.usage);

    return html`
      <section class="assistant-panel">
        <div class="assistant-shell">
          ${this.renderRail()} ${this.renderRailGrip()} ${this.renderRailToggle()}
          <div class="assistant-card">
          ${this.configured === false
            ? html`<div class="assistant-note assistant-note-standalone">
                The assistant is not switched on for this deployment yet. Nothing else in
                the app is affected - ask whoever administers it to enable it.
              </div>`
            : nothing}

          <!-- The scroller is the full width of the page and the reading column
               sits inside it, so the scrollbar rides the right-hand edge of the
               window where people reach for it - rather than halfway across,
               against the edge of the text. -->
          <div class="assistant-log scroll-thin" @scroll=${(e: Event) => this.onLogScroll(e)}>
            <div class="assistant-log-inner">
            ${this.turns.length === 0 && !this.busy ? this.renderEmpty(ship) : nothing}
            ${this.turns.map((t, index) => this.renderTurn(t, index))}
            ${this.busy
              ? html`<div class="assistant-turn assistant-turn-assistant">
                  <!-- The compass rose, turning and breathing. The wait is ten
                       to thirty seconds and of unknown length, so this says
                       something is happening without pretending to know how far
                       along it is - and it is the same animation the minimised
                       mark uses, so "the assistant is working" looks like one
                       thing wherever you see it. -->
                  <div class="assistant-working" role="status" aria-live="polite">
                    ${assistantMark(34)}
                    <!-- What it is really doing, one line, replaced as each step
                         starts. Keyed so every new step fades in afresh. -->
                    ${keyed(
                      this.step,
                      html`<span class="assistant-working-step">${this.step ? `${this.step}…` : 'Working on your question…'}</span>`,
                    )}
                  </div>
                </div>`
              : nothing}
            ${hasUsage && assistantTechnical.value
              ? html`<p class="assistant-session" title="Tokens and cost, this conversation">
                  ${(session.input + session.output).toLocaleString()} tokens ·
                  $${session.cost.toFixed(4)} this conversation
                </p>`
              : nothing}
            </div>
          </div>

          <div class="assistant-composer">
            <!-- Only while you are away from the newest message. A control that
                 is always there is a control nobody reads. -->
            ${!this.atBottom && this.turns.length
              ? html`<button
                  class="assistant-jump"
                  title="Jump to the latest message"
                  aria-label="Jump to the latest message"
                  @click=${() => this.scrollToEnd()}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M8 3v9.5M8 12.5 3.8 8.3M8 12.5l4.2-4.2"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Latest
                </button>`
              : nothing}
            ${this.error ? html`<div class="assistant-error">${this.error}</div>` : nothing}
            <div class="assistant-composer-box">
              ${this.renderMentionMenu()}
              <div class="assistant-input-stack">
                <!-- No whitespace inside this element. It wraps as pre-wrap,
                     so the template's own newline and indentation would render
                     as an empty first line and push every word out of the
                     pill. -->
                <!-- prettier-ignore -->
                <div class="assistant-highlight" aria-hidden="true">${this.renderTagged(this.input, this.picked)}<span class="assistant-highlight-tail"></span></div>
              <textarea
                class="assistant-input"
                rows="1"
                placeholder="Ask about your fleet… type @ for a ship or a signal"
                aria-label="Describe the plot or export you want"
                aria-autocomplete="list"
                aria-expanded=${this.menu.length > 0}
                aria-controls="assistant-mentions"
                .value=${this.input}
                @input=${(e: Event) => this.onComposerInput(e)}
                @click=${(e: Event) => this.refreshMenu(e.target as HTMLTextAreaElement)}
                @blur=${() => this.closeMenuSoon()}
                @keydown=${(e: KeyboardEvent) => this.onKeydown(e)}
                @scroll=${(e: Event) => this.syncHighlightScroll(e.target as HTMLTextAreaElement)}
              ></textarea>
              </div>
              <!-- Round, inside the pill, and marked with an arrow rather than a
                   word: at this size a label would set the height of the whole
                   composer. Disabled until there is something to send. -->
              <button
                class="assistant-send"
                title="Send"
                aria-label="Send"
                ?disabled=${this.busy || !this.input.trim()}
                @click=${() => void this.send()}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M8 13V3.5M8 3.5 3.8 7.7M8 3.5l4.2 4.2"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
            <p class="assistant-hint">Enter to send · Shift+Enter for a new line · @ to tag a ship</p>
          </div>
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'data-assistant': DataAssistant;
  }
}
